"use client";

import {
  createUserAssociation,
  deleteUserAssociation,
  getUserAssociations,
} from "deso-protocol";
import Link from "next/link";
import { useContext, useState } from "react";
import {
  LuAxe,
  LuBox,
  LuDollarSign,
  LuEllipsis as LuMoreHorizontal,
  LuExternalLink,
  LuEye,
  LuEyeOff,
  LuFlame,
  LuLock,
  LuSend,
  LuTarget,
  LuUsers as LuUsers2,
  LuWrench,
} from "react-icons/lu";

import { waitForTxn } from "@/backend/api-helpers";
import { setDialog } from "@/components/app/dialogs/dialog-provider";
import { CreatorCoinTab } from "@/components/shared/creator-coin-exchange-modal/creator-coin-tabs";
import { IsLoggedIn } from "@/components/shared/is-logged-in";
import { Button } from "@/components/ui/button";
import {
  DropdownDrawer,
  DropdownDrawerContent,
  DropdownDrawerItem,
  DropdownDrawerTrigger,
} from "@/components/ui/dropdown-drawer";
import { createToastErrorDetails } from "@/components/ui/toast-helpers";
import { toast } from "@/components/ui/use-toast";
import { UserContext } from "@/contexts/user-context";
import {
  APP_ROUTES,
  AssociationDeSoTokenWhitelistValue,
  AssociationType,
  DESO_TOKEN_NAME,
} from "@/utils/constants";
import { DIALOG } from "@/utils/dialog-routes";
import { isDesoFoundationToken, isWrappedAsset } from "@/utils/helpers";

interface TradeCoinActionsProps {
  coinPublicKey: string;
  coinUsername: string;
  hasAmm: boolean;
  refetchData?: () => void;
  isMyWallet?: boolean;
  isWhitelist?: boolean;
  hideWalletActions?: boolean;
  setLoading?: (loading: boolean) => void;
  traderUserName?: string;
  traderPublicKey?: string;
  onSuccess?: () => void;
}

const TradeCoinActions = ({
  coinUsername,
  coinPublicKey,
  hasAmm,
  refetchData,
  isMyWallet = false,
  isWhitelist = true,
  hideWalletActions = false,
  setLoading,
  traderUserName,
  traderPublicKey,
  onSuccess = () => {},
}: TradeCoinActionsProps) => {
  const [open, setOpen] = useState(false);
  const { user, currentUserPublicKey } = useContext(UserContext);

  const handleRefetchData = async () => {
    await refetchData?.();
    setOpen(false);
  };

  const setWhitelistStatus = async () => {
    setLoading?.(true);

    const payload = {
      TransactorPublicKeyBase58Check: currentUserPublicKey,
      TargetUserPublicKeyBase58Check: coinPublicKey,
      AssociationType: AssociationType.DeSoTokenWhitelistAssociationKey,
      AssociationValue:
        AssociationDeSoTokenWhitelistValue.DeSoTokenWhitelistAssociationKey,
    };

    try {
      if (isWhitelist) {
        const { Associations } = await getUserAssociations(payload);

        if (!Associations?.length) {
          toast({
            title: "Error",
            description: `The coin cannot be moved right now. Please try again later.`,
            variant: "destructive",
          });

          setLoading?.(false);

          return;
        }

        const [{ TransactorPublicKeyBase58Check, AssociationID }] =
          Associations;

        const data = await deleteUserAssociation({
          TransactorPublicKeyBase58Check,
          AssociationID,
        });

        if (!data.submittedTransactionResponse?.TxnHashHex) {
          throw new Error(
            "Your coin was not moved properly. Please try again.",
          );
        }

        await waitForTxn({
          transactionHash: data.submittedTransactionResponse.TxnHashHex,
        });

        toast({
          title: "Success",
          description: `${coinUsername} was successfully moved to your Hidden Tokens`,
          variant: "success",
        });
      } else {
        const data = await createUserAssociation({
          TransactorPublicKeyBase58Check: currentUserPublicKey,
          TargetUserPublicKeyBase58Check: coinPublicKey,
          AssociationType: AssociationType.DeSoTokenWhitelistAssociationKey,
          AssociationValue:
            AssociationDeSoTokenWhitelistValue.DeSoTokenWhitelistAssociationKey,
        });

        if (!data.submittedTransactionResponse?.TxnHashHex) {
          throw new Error(
            "Your coin was not moved properly. Please try again.",
          );
        }

        await waitForTxn({
          transactionHash: data.submittedTransactionResponse.TxnHashHex,
        });

        toast({
          title: "Success",
          description: `${coinUsername} was successfully moved to your ${DESO_TOKEN_NAME}s`,
          variant: "success",
        });
      }

      await handleRefetchData();
    } catch (e) {
      toast({
        variant: "destructive",
        title: "Error",
        description: `An error happened when moving your ${DESO_TOKEN_NAME}s`,
        details: createToastErrorDetails(e),
      });

      setLoading?.(false);
    }
  };

  return (
    <DropdownDrawer
      historyName="trade-coin-actions"
      open={open}
      onOpenChange={setOpen}
      title="Token actions"
    >
      <DropdownDrawerTrigger
        render={
          <Button
            variant="outline"
            size="icon"
            className="
              size-full rounded-full border-none text-sm
              hover:bg-transparent
            "
          >
            <LuMoreHorizontal className="size-4" />
          </Button>
        }
      />
      <DropdownDrawerContent sideOffset={-8}>
        {coinPublicKey === currentUserPublicKey && (
          <>
            <DropdownDrawerItem
              render={
                <Link
                  href={`${APP_ROUTES.settingsTradeCoin}`}
                  className="
                    flex items-center gap-2 text-inherit
                    hover:bg-popover-foreground hover:text-primary
                  "
                >
                  <LuWrench className="size-4 text-muted!" />
                  Manage Your Token
                </Link>
              }
            />
            <DropdownDrawerItem
              disabled={user?.daoCoinMintingDisabled || false}
              onClick={() =>
                setDialog(DIALOG.TradeCoinMint, {
                  open: true,
                  replace: true,
                  props: {
                    onMintSuccess: handleRefetchData,
                  },
                })
              }
            >
              <LuAxe className="size-4 text-muted!" />
              {user?.daoCoinMintingDisabled
                ? "Minting is disabled"
                : "Mint New Coins"}
            </DropdownDrawerItem>
          </>
        )}
        <IsLoggedIn>
          {!isWrappedAsset(coinPublicKey) &&
            !isDesoFoundationToken(coinPublicKey) &&
            hasAmm &&
            Object.values(CreatorCoinTab).map((item) => {
              return (
                <DropdownDrawerItem
                  key={item}
                  className="text-inherit capitalize"
                  onClick={() => {
                    setDialog(DIALOG.TradeCoin, {
                      open: true,
                      replace: true,
                      params: {
                        publicKey: coinPublicKey,
                        tab: item,
                      },
                      props: {
                        onSuccess,
                      },
                    });
                  }}
                >
                  {item === CreatorCoinTab.Buy && (
                    <LuBox className="size-4 text-muted" />
                  )}
                  {item === CreatorCoinTab.Sell && (
                    <LuTarget className="size-4 text-muted" />
                  )}
                  {item === CreatorCoinTab.Transfer && (
                    <LuSend className="size-4 text-muted" />
                  )}
                  {item === CreatorCoinTab.Lock && (
                    <LuLock className="size-4 text-muted" />
                  )}
                  {item === CreatorCoinTab.Burn && (
                    <LuFlame className="size-4 text-muted" />
                  )}
                  {item}
                </DropdownDrawerItem>
              );
            })}
        </IsLoggedIn>
        <DropdownDrawerItem
          className="text-inherit"
          render={
            <Link
              href={`${process.env.NEXT_PUBLIC_OPENFUND_URI}/trade/${coinUsername}`}
              className="flex items-center gap-2"
              target="_blank"
            >
              <LuExternalLink className="size-4 text-muted" />
              Advanced Trade
            </Link>
          }
        />
        {coinPublicKey === process.env.NEXT_PUBLIC_DESO_ZERO_PUBLIC_KEY ? (
          <DropdownDrawerItem
            className="text-inherit"
            render={
              <Link
                href={`${process.env.NEXT_PUBLIC_EXPLORER_URI}/stats/coin-holders`}
                className="flex items-center gap-2"
                target="_blank"
              >
                <LuExternalLink className="size-4 text-muted" />
                View Holders
              </Link>
            }
          />
        ) : (
          <DropdownDrawerItem
            className="text-inherit"
            onClick={() =>
              setDialog(DIALOG.Holders, {
                open: true,
                replace: true,
                params: {
                  publicKey: coinPublicKey,
                  username: coinUsername,
                },
              })
            }
          >
            <LuUsers2 className="size-4 text-muted" />
            View Holders
          </DropdownDrawerItem>
        )}
        {!hideWalletActions && isMyWallet && (
          <DropdownDrawerItem
            className="text-inherit"
            onClick={setWhitelistStatus}
          >
            {isWhitelist ? (
              <LuEyeOff className="size-4 text-muted" />
            ) : (
              <LuEye className="size-4 text-muted" />
            )}
            Move To {isWhitelist ? "Hidden" : "My Tokens"}
          </DropdownDrawerItem>
        )}
        {(traderPublicKey || traderUserName) && (
          <DropdownDrawerItem
            className="text-inherit"
            render={
              <Link
                href={`${APP_ROUTES.tradePortfolio}/${traderUserName ?? traderPublicKey}`}
                className="flex items-center gap-2"
              >
                <LuDollarSign className="size-4 text-muted" />
                View Trader
              </Link>
            }
          />
        )}
      </DropdownDrawerContent>
    </DropdownDrawer>
  );
};

export default TradeCoinActions;
